import RequestApi from 'lib/labelify-api';

/**
 * List foods
 * @param {object} params 
 */
export async function ListFoods({ params = {}, sessionId = null, responseHandler = null } = {}) {
  const [responseData, response] = await RequestApi({
    url: 'foods',
    body: {
      short: params.short ?? null,
      all: params.all ?? null,
      lang: params.lang ?? null,
      search: params.search ?? null,
      tags: params.tags ?? null,
      recipe_id: params.recipe_id ?? null,
      page: params.page ?? null,
      page_size: params.page_size ?? null,
      order_by: params.orderBy ?? null,
      expand: params.expand ?? null,
    },
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : false;
}

/**
 * Add food
 * @param {object} data 
 */
export async function AddFood({ data = {}, foodIdToDuplicate = null, recipeIdToCopy = null, responseHandler = null, noDefaultName = false, sessionId = null }) {
  let url = 'foods';
  let params = [];

  // NOTE - body is ignored when sending food_id_to_duplicate or recipe_id_to_subrecipe

  if (foodIdToDuplicate != null) {
    params.push('food_id_to_duplicate=' + encodeURIComponent(foodIdToDuplicate));
  }
  else if (recipeIdToCopy != null) {
    params.push('recipe_id_to_subrecipe=' + encodeURIComponent(recipeIdToCopy));
  }

  // Default name is used to overwrite the behavior when copying
  if ((foodIdToDuplicate != null || recipeIdToCopy != null) && data.name) {
    params.push('rename=' + encodeURIComponent(data.name));
  }

  if (noDefaultName) {
    params.push('no_default_name=1');
  }

  if (params.length) {
    url += '?' + params.join('&');
  }

  const [responseData, response] = await RequestApi({
    url,
    body: {
      name: data.name ?? null,
      product_code: data.product_code ?? null,
      name_en: data.name_en ?? null,
      name_fr: data.name_fr ?? null,
      common_name_en: data.common_name_en ?? null,
      common_name_fr: data.common_name_fr ?? null,
      public: data.public ?? null,
      irradiated: data.irradiated ?? null,
      irradiated_fr_feminine: data.irradiated_fr_feminine ?? null,
      irradiated_fr_plural: data.irradiated_fr_plural ?? null,
      thawed: data.thawed ?? null,
      brand: data.brand ?? null,
      source: data.source ?? null,
      mass_quantity: data.mass_quantity ?? null,
      mass_unit: data.mass_unit ?? null,
      volume_quantity: data.volume_quantity ?? null,
      volume_unit: data.volume_unit ?? null,
      custom_units: data.custom_units ?? null,
      refuse_pct: data.refuse_pct ?? null,
      refuse_description_en: data.refuse_description_en ?? null,
      refuse_description_fr: data.refuse_description_fr ?? null,
      ingredient_list_en: data.ingredient_list_en ?? null,
      ingredient_list_fr: data.ingredient_list_fr ?? null,
      nutriments: data.nutriments ?? null,
      allergies: data.allergies ?? null,
      may_contains: data.may_contains ?? null,
      density_overwrite: data.density_overwrite ?? null,
      notes_en: data.notes_en ?? null,
      notes_fr: data.notes_fr ?? null,
    },
    method: 'POST',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 201) ? responseData : false;
}

/**
 * Get food
 */
export async function GetFood({ foodId, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId,
    sessionId: sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : null;
}

/**
 * Edit food
 */
export async function EditFood({ foodId, data, responseHandler = null, patch = false, sessionId = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId,
    body: {
      name: data.name ?? null,
      product_code: data.product_code ?? null,
      name_en: data.name_en ?? null,
      name_fr: data.name_fr ?? null,
      common_name_en: data.common_name_en ?? null,
      common_name_fr: data.common_name_fr ?? null,
      public: data.public ?? null,
      irradiated: data.irradiated ?? null,
      irradiated_fr_feminine: data.irradiated_fr_feminine ?? null,
      irradiated_fr_plural: data.irradiated_fr_plural ?? null,
      thawed: data.thawed ?? null,
      brand: data.brand ?? null,
      source: data.source ?? null,
      mass_quantity: data.mass_quantity ?? null,
      mass_unit: data.mass_unit ?? null,
      volume_quantity: data.volume_quantity ?? null,
      volume_unit: data.volume_unit ?? null,
      custom_units: data.custom_units ?? null,
      refuse_pct: data.refuse_pct ?? null,
      refuse_description_en: data.refuse_description_en ?? null,
      refuse_description_fr: data.refuse_description_fr ?? null,
      ingredient_list_en: data.ingredient_list_en ?? null,
      ingredient_list_fr: data.ingredient_list_fr ?? null,
      nutriments: data.nutriments ?? null,
      allergies: data.allergies ?? null,
      may_contains: data.may_contains ?? null,
      density_overwrite: data.density_overwrite ?? null,
      notes_en: data.notes_en ?? null,
      notes_fr: data.notes_fr ?? null,
      //version_id: data.version_id ?? null,
    },
    method: (patch) ? 'PATCH' : 'POST',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : false;
}

/**
 * Remove food
 */
export async function RemoveFood({ foodId, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId,
    method: 'DELETE',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 204);
}

/**
 * Batch remove food
 */
export async function BatchRemoveFoods({ foodIds, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/batch_remove?ids=' + foodIds.join(','),
    method: 'POST',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : false;
}

/**
 * Batch CSV export
 */
export async function BatchCsvExportFoods({ foodIds, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/batch_csv_export?ids=' + foodIds.join(','),
    method: 'POST',
    sessionId,
    responseHandler,
    jsonResponse: false,
    useTimeout: false,
  })

  return (response !== null && response.status == 200) ? response : false;
}

/**
 * Add custom unit
 */
export async function AddCustomUnit({ foodId, data, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/custom_units',
    body: {
      name_en: data.name_en ?? null,
      name_fr: data.name_fr ?? null,
      rate: data.rate ?? null,
      unit_to: data.unit_to ?? null,
      //food_version_id: data.food_version_id ?? null,
    },
    method: 'POST',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 201) ? responseData : false;
}

/**
 * Get custom unit
 */
export async function GetCustomUnit({ foodId, customUnitId, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/custom_units/' + customUnitId,
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : false;
}

/**
 * Edit custom unit
 */
export async function EditCustomUnit({ foodId, customUnitId, data, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/custom_units/' + customUnitId,
    body: {
      name_en: data.name_en ?? null,
      name_fr: data.name_fr ?? null,
      rate: data.rate ?? null,
      unit_to: data.unit_to ?? null,
      //food_version_id: data.food_version_id ?? null,
    },
    method: 'POST',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : false;
}

/**
 * Remove custom unit
 */
export async function RemoveCustomUnit({ foodId, customUnitId, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/custom_units/' + customUnitId,
    method: 'DELETE',
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 204);
}

/**
 * Download technical file
 */
export async function DownloadTechnicalFile({ foodId, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/technical_file',

    jsonResponse: false,
  })

  return (response !== null && response.status == 200) ? response : false;
}

/**
 * Add technical file
 */
export async function AddTechnicalFile({ foodId, fileData, sessionId = null, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/technical_file',
    body: {
      upload_file: fileData
    },
    method: 'POST',
    multipart: true,
    sessionId,
    responseHandler,
  })

  return (response !== null && response.status == 201) ? responseData : false;
}

/**
 * Remove technical file
 */
export async function RemoveTechnicalFile({ foodId, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/technical_file',
    method: 'DELETE',
    responseHandler,
  })

  return (response !== null && response.status == 204) ? responseData : false;
}

/**
 * Update subrecipe
 */
export async function UpdateSubrecipe({ foodId, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/subrecipe',
    method: 'POST',
    responseHandler,
  })

  return (response !== null && response.status == 200) ? responseData : false;
}

/**
 * Unlink subrecipe
 */
export async function UnlinkSubrecipe({ foodId, responseHandler = null }) {
  const [responseData, response] = await RequestApi({
    url: 'foods/' + foodId + '/subrecipe',
    method: 'DELETE',
    responseHandler,
  })

  return (response !== null && response.status == 204) ? responseData : false;
}

/**
 * Food name
 * Helper function to resolve name vs name_en vs name_fr
 */
export function FoodName(food, locale = 'en') {
  const other = locale == 'en' ? 'fr' : 'en';
  return food.name ?? (food['name_' + locale] ?? food['name_' + other]);
}

/**
 * Ingredient name
 * Same as FoodName, adapted for ingredient
 */
export function IngredientName(ingredient, locale = 'en') {
  const other = locale == 'en' ? 'fr' : 'en';
  return ingredient.food_name ?? (ingredient['food_name_' + locale] ?? ingredient['food_name_' + other]);
}
